import React, { useState } from "react";
import { View, ScrollView, Pressable } from "react-native";
import StyledText from "../../common/ui/styled-text";
import { ICall } from "../../../types/call";
import { formatPhoneNumber } from "react-phone-number-input";
import { displayPrice } from "../../../utils/price-helpers";
import { Badge } from "../../badge";
import { useFormattedDate } from "../../../hooks/useFormattedDate";
import {
  hasBeenRefunded,
  isCallCompleted,
  isExpired,
  isOrderSubmittedViaTablet,
  isPrepaid,
  isVoided,
} from "../../../utils/call-helpers";
import {
  Ban,
  Calendar,
  CircleCheck,
  CircleX,
  Flag,
  HandCoins,
  History,
  Info,
  Loader2,
  MapPin,
  MoreVertical,
  Phone,
  ShoppingBag,
  Truck,
  X,
  Zap,
} from "lucide-react-native";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../common/shadcn/dropdown";
import ChargeModal from "./charge-modal";
import { isStreamOrderingAgent } from "../../../utils/agent-helpers";
import { useSettingsData } from "../../../contexts/SettingsDataContext";
import { useActiveAgent } from "../../../contexts/ActiveAgentContext";

interface CallDetailsPanelProps {
  call: ICall;
  onClose: () => void;
  onViewHistory: (callId: string, phone: string) => void;
  onReportCall: (callId: string, callTimestamp: string) => void;
  onRefund: (call: ICall) => void;
  onBlockCaller: (phone: string) => void;
  onUnblockCaller: (phone: string) => void;
  isCallerBlocked: (phone: string) => boolean;
  isBlockUnblockLoading: (phone: string) => boolean;
}

const CallDetailsPanel: React.FC<CallDetailsPanelProps> = ({
  call,
  onClose,
  onViewHistory,
  onReportCall,
  onRefund,
  onBlockCaller,
  onUnblockCaller,
  isCallerBlocked,
  isBlockUnblockLoading,
}) => {
  const { formatTime, formatDateLong } = useFormattedDate();
  const hasOrder = call.order != null;
  const orderId = call.order?.id.slice(-4).toUpperCase();
  const isDelivery = call.order?.deliveryAddress != null;
  const isScheduled = call.order?.futureOrderDate != null;

  // Modal state
  const [isChargeModalVisible, setIsChargeModalVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { activeAgent } = useActiveAgent();
  const { settings } = useSettingsData();
  const isStreamOrdering = isStreamOrderingAgent(
    activeAgent?.type,
    settings?.posProvider === "stream"
  );

  const handleCharge = () => {
    setIsChargeModalVisible(true);
  };

  const handleRefund = () => {
    onRefund(call);
  };

  const handleChargeSubmit = async (amount: number, reason: string) => {
    setIsProcessing(true);
    try {
      // TODO: Implement charge API call
      console.log("Charging amount:", amount, "Reason:", reason);
      // await chargeCustomer(call.id, amount, reason);
      setIsChargeModalVisible(false);
    } catch (error) {
      console.error("Error charging customer:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getFulfillmentBadge = () => {
    if (isDelivery) {
      return (
        <Badge
          variant="outline"
          className="bg-purple-50 text-purple-700 border-purple-200 gap-1"
        >
          <Truck size={12} />
          Delivery
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-blue-50 text-blue-700 border-blue-200 gap-1"
      >
        <ShoppingBag size={12} />
        Pickup
      </Badge>
    );
  };

  const getTimingBadge = () => {
    if (isScheduled) {
      return (
        <Badge
          variant="outline"
          className="bg-white text-gray-700 border-gray-200 gap-1"
        >
          <Calendar size={12} />
          Scheduled
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-amber-50 text-amber-700 border-amber-200 gap-1"
      >
        <Zap size={12} />
        ASAP
      </Badge>
    );
  };

  const formattedDeliveryAddress = call.order?.deliveryAddress
    ? `${call.order.deliveryAddress.addressLine1}${
        call.order.deliveryAddress.addressLine2
          ? `, ${call.order.deliveryAddress.addressLine2}`
          : ""
      }, ${call.order.deliveryAddress.city}`
    : "";

  const renderModifiers = (modifiers: any[], level: number = 0) => {
    if (!modifiers || modifiers.length === 0) return null;

    return (
      <View className={`${level === 0 ? "mt-1" : "mt-0"}`}>
        {modifiers.map((modifier, modifierIndex) => (
          <View key={modifierIndex}>
            <StyledText
              className="text-xs text-gray-700"
              style={{ marginLeft: level * 16 }}
            >
              - {modifier.quantity > 1 ? `${modifier.quantity}x ` : ""}
              {modifier.name}
            </StyledText>
            {renderModifiers(modifier.modifiers, level + 1)}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View className="w-96 bg-white border-l border-gray-200 flex flex-col h-full shadow-md">
      <View>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                call.order != null ? "bg-green-100" : "bg-blue-100"
              }`}
            >
              {call.customerName != null ? (
                <span
                  className={`font-semibold text-sm ${
                    call.order != null ? "text-green-600" : "text-blue-600"
                  }`}
                >
                  {call.customerName?.charAt(0).toUpperCase()}
                </span>
              ) : (
                <Phone size={16} color="#6b7280" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {call.customerName}
              </h3>
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-gray-500 underline">
                  {formatPhoneNumber(call.phone)}
                </p>
                {isCallerBlocked(call.phone) && (
                  <Ban size={14} color="#ef4444" />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Pressable className="p-2 hover:bg-gray-100 rounded-full">
                  <MoreVertical size={16} color="#6b7280" />
                </Pressable>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onSelect={() => {
                    onViewHistory(call.id, call.phone);
                  }}
                >
                  <History size={16} className="mr-2" />
                  View Caller History
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    if (isCallerBlocked(call.phone)) {
                      onUnblockCaller(call.phone);
                    } else {
                      onBlockCaller(call.phone);
                    }
                  }}
                  disabled={isBlockUnblockLoading(call.phone)}
                >
                  {isBlockUnblockLoading(call.phone) ? (
                    <Loader2
                      size={16}
                      className="mr-2 text-gray-700 animate-spin [transform-origin:center]"
                    />
                  ) : isCallerBlocked(call.phone) ? (
                    <CircleCheck size={16} className="mr-2" />
                  ) : (
                    <Ban size={16} className="mr-2" />
                  )}
                  {isBlockUnblockLoading(call.phone)
                    ? "Loading..."
                    : isCallerBlocked(call.phone)
                      ? "Unblock Caller"
                      : "Block Caller"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Pressable
              className="p-2 hover:bg-gray-100 rounded-full"
              onPress={onClose}
            >
              <X size={16} color="#6b7280" />
            </Pressable>
          </div>
        </div>
      </View>

      <ScrollView className="flex-1 p-4 pr-6">
        {!isCallCompleted(call) && (
          <div className="mb-4 pb-4 border-b border-gray-100">
            <p className="text-base text-gray-600 text-center italic">
              This call is ongoing...
            </p>
          </div>
        )}

        {isStreamOrdering && isOrderSubmittedViaTablet(call) && (
          <View className="mb-6">
            <View className="flex-row items-center gap-2 mt-3 p-3 bg-white/95 rounded-md border border-border">
              <Info size={16} color="#111827" />
              <StyledText className="">
                Check your POS to see the order details.
              </StyledText>
            </View>
          </View>
        )}

        {hasBeenRefunded(call) && (
          <View className="mb-4">
            <View className="flex-row items-center gap-2 p-3 bg-white/95 rounded-md border border-orange-200">
              <HandCoins size={16} color="#fb923c" />
              <StyledText className="text-orange-700">
                This customer has been refunded.
              </StyledText>
            </View>
          </View>
        )}

        {isExpired(call) && (
          <View className="mb-4">
            <View className="flex-row items-center gap-2 p-3 bg-red-50 rounded-md border border-red-200">
              <CircleX size={20} color="#dc2626" />
              <StyledText className="text-red-700 font-medium text-xs">
                The customer did not complete payment within 30 minutes, so the
                order has expired.
              </StyledText>
            </View>
          </View>
        )}

        {isVoided(call) && (
          <View className="mb-4">
            <View className="flex-row items-center gap-2 p-3 bg-red-50 rounded-md border border-red-200">
              <CircleX size={20} color="#dc2626" />
              <StyledText className="text-red-700 font-medium text-xs">
                This order has been voided.
              </StyledText>
            </View>
          </View>
        )}

        {hasOrder && !isOrderSubmittedViaTablet(call) && (
          <View className="mb-6">
            <View className="bg-gray-50 p-4 rounded-lg">
              <View className="flex-row items-center justify-between mb-3">
                <View className="flex-row items-center gap-2">
                  <ShoppingBag size={16} color="#22c55e" />
                  <StyledText className="font-semibold text-gray-700">
                    Order #{orderId}
                  </StyledText>
                  {isPrepaid(call) && (
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-200 gap-1"
                    >
                      Prepaid
                    </Badge>
                  )}
                </View>
                {call.order != null && call.order.total && (
                  <StyledText className="text-sm font-semibold text-gray-700">
                    {displayPrice(call.order.total)}
                  </StyledText>
                )}
              </View>

              <View className="space-y-3">
                {call.order?.items.map((item, index) => (
                  <View
                    key={index}
                    className="border-b border-gray-100 pb-2 last:border-b-0"
                  >
                    <View className="flex-row items-start gap-2">
                      <StyledText className="font-medium text-sm text-gray-700 min-w-[2rem]">
                        {item.quantity}x
                      </StyledText>
                      <View className="flex-1">
                        <StyledText className="text-sm text-gray-700 font-medium">
                          {item.name}
                        </StyledText>
                        {renderModifiers(item.modifiers)}
                        {item.specialInstructions?.length > 0 && (
                          <StyledText className="text-xs text-gray-600 mt-1 italic">
                            "{item.specialInstructions}"
                          </StyledText>
                        )}
                      </View>
                    </View>
                  </View>
                ))}
              </View>

              <View className="flex-row gap-2 mt-4">
                {getFulfillmentBadge()}
                {getTimingBadge()}
              </View>

              {isDelivery && (
                <View className="flex-row items-center gap-2 mt-3 p-3 bg-white/95 rounded-md border border-gray-200">
                  <MapPin size={12} color="#6b7280" />
                  <StyledText className="text-xs text-gray-800 flex-1 font-medium">
                    {formattedDeliveryAddress}
                  </StyledText>
                </View>
              )}
            </View>
            <View className="flex flex-row gap-1 ml-3 justify-end">
              {/* <Pressable
                onPress={handleCharge}
                className={`group h-7 px-2 flex-row items-center justify-center rounded ${"active:bg-green-50 active:text-green-700 hover:bg-green-50"}`}
              >
                <BanknoteArrowUp
                  size={12}
                  className={`mr-1 group-hover:text-green-700 text-gray-600`}
                />
                <StyledText className="text-xs text-gray-600 group-hover:text-green-700">
                  Charge Extra
                </StyledText>
              </Pressable> */}

              {isPrepaid(call) && (
                <Pressable
                  onPress={handleRefund}
                  className={`group h-7 px-2 flex-row items-center justify-center rounded ${"active:bg-red-50 hover:bg-red-50"}`}
                >
                  <HandCoins
                    size={12}
                    className={`mr-1 group-hover:text-red-700 text-gray-600`}
                  />
                  <StyledText className="text-xs text-gray-600 active:text-red-700 group-hover:text-red-700">
                    Refund
                  </StyledText>
                </Pressable>
              )}
            </View>
          </View>
        )}

        {isCallCompleted(call) && (
          <View className="mb-4">
            <StyledText className="font-semibold text-gray-900 mb-2">
              Conversation Summary
            </StyledText>
            <StyledText className="text-gray-700 leading-relaxed pr-4">
              {call.summary || "No conversation summary available"}
            </StyledText>
          </View>
        )}

        {isCallCompleted(call) && (
          <div className="mb-4 pb-4 border-b border-gray-100">
            <p className="text-xs italic text-gray-600">
              Call ended at {formatTime(call.timestamp)} on{" "}
              {formatDateLong(call.timestamp)}
            </p>
          </div>
        )}

        <View className="space-y-3">
          {call.audioUrl && (
            <audio src={call.audioUrl} controls className="w-full" />
          )}
          {/* <PlayAudioButton
            isPlaying={isPlaying}
            onPress={() => onPlayPause(call.audioUrl, call.id)}
            disabled={!call.audioUrl}
            duration={call.duration}
            isLoading={isLoading}
          /> */}

          {isCallCompleted(call) && (
            <View className="flex flex-row gap-3">
              <Pressable
                className="px-4 py-3 rounded-full border border-gray-300 flex-row items-center justify-center bg-white hover:bg-gray-50 flex-1"
                onPress={() => onReportCall(call.id, call.timestamp)}
              >
                <Flag size={16} color="#6b7280" />
                <StyledText className="ml-2 text-gray-700">
                  Report Call
                </StyledText>
              </Pressable>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Charge Modal */}
      <ChargeModal
        visible={isChargeModalVisible}
        onClose={() => setIsChargeModalVisible(false)}
        onSubmit={handleChargeSubmit}
        isLoading={isProcessing}
        callerName={call.customerName}
        phone={call.phone}
      />
    </View>
  );
};

export default CallDetailsPanel;
